import { CheckboxCheckedSVG, CheckboxUncheckedSVG } from "@/assets/svg";
import React, { useState, useRef, useEffect } from "react";

interface Option {
  value: string;
  label: string;
}

interface SelectWithCheckboxProps {
  label: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  required?: boolean;
  id?: string;
  placeholder?: string;
  maxHeight?: string;
  disabled?: boolean;
  showAbove?: boolean;
  /**
   * When true, allows typing arbitrary values into the main field.
   * Press Enter or comma to convert the current text into a chip.
   */
  allowCustomInput?: boolean;
  customValueValidator?: (value: string) => boolean;
  customValueErrorMessage?: string;
  /**
   * When true with allowCustomInput, hides search + checkbox list (chips + typing only).
   */
  hideOptionsDropdown?: boolean;
}

const SelectWithCheckbox: React.FC<SelectWithCheckboxProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  id,
  placeholder = "Select options",
  maxHeight = "450px",
  disabled = false,
  showAbove = true,
  allowCustomInput = false,
  customValueValidator,
  customValueErrorMessage = "Invalid value",
  hideOptionsDropdown = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([
    ...options,
  ]);
  const [customInput, setCustomInput] = useState("");
  const [customInputError, setCustomInputError] = useState("");
  const selectRef = useRef<HTMLDivElement>(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedOptions: Option[] = value.map((val) => {
    const fromOptions = options.find((o) => o.value === val);
    return fromOptions || { value: val, label: val };
  });
  const handleAddCustomValue = () => {
    if (!allowCustomInput || disabled) return;
    const trimmed = customInput.trim();
    if (!trimmed) return;
    if (customValueValidator && !customValueValidator(trimmed)) {
      setCustomInputError(customValueErrorMessage);
      return;
    }
    if (value.includes(trimmed)) {
      setCustomInput("");
      setCustomInputError("");
      return;
    }
    onChange([...value, trimmed]);
    setCustomInput("");
    setCustomInputError("");
  };
  // Track window resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Check position when opening the dropdown
  // useEffect(() => {
  //   if (isOpen && selectRef.current) {
  //     const selectRect = selectRef.current.getBoundingClientRect();
  //     const windowHeight = window.innerHeight;
  //     const spaceBelow = windowHeight - selectRect.bottom;

  //     // Use actual maxHeight value or default if not provided
  //     const maxHeightValue = parseInt(maxHeight) || 250;

  //     // If there's not enough space below, show above
  //     // Add extra buffer (20px) to account for padding and borders
  //     setShowAbove(
  //       spaceBelow < maxHeightValue + 20 && selectRect.top > maxHeightValue + 20
  //     );
  //   }
  // }, [isOpen, maxHeight, options.length]);

  // Filter options based on search term while preserving original order
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions([...options]);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = options.filter((option) => {
        return option.label.toLowerCase().includes(searchLower);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (hideOptionsDropdown) return;
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search and filtered options when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions([...options]);
    }
  }, [isOpen, options, hideOptionsDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (hideOptionsDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, hideOptionsDropdown]);

  // Handle checkbox change
  const handleCheckboxChange = (optionValue: string) => {
    if (!disabled) {
      const newValue = [...value];
      const index = newValue.indexOf(optionValue);

      if (index === -1) {
        // Add the value if it's not already selected
        newValue.push(optionValue);
      } else {
        // Remove the value if it's already selected
        newValue.splice(index, 1);
      }

      onChange(newValue);
    }
  };

  // Remove a selected item
  const removeSelectedItem = (optionValue: string) => {
    if (!disabled) {
      const newValue = value.filter((val) => val !== optionValue);
      onChange(newValue);
    }
  };

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[#1E1E1E] text-[14px] font-[500] mb-1"
      >
        {label}
        {required && <span className="text-[#DC2626]">*</span>}
      </label>
      <div className="relative" ref={selectRef}>
        <div
          className={`w-full min-h-[42px] px-2.5 py-2.5 border ${
            error || customInputError ? "border-[#FF3B30]" : "border-[#E0E0E0]"
          } rounded-[6px] flex flex-wrap items-center justify-between ${
            disabled
              ? ""
              : hideOptionsDropdown && allowCustomInput
                ? "cursor-text"
                : "cursor-pointer"
          }`}
          onClick={() => {
            if (disabled || hideOptionsDropdown) return;
            setIsOpen(!isOpen);
          }}
        >
          <div className="flex flex-wrap gap-1 pr-8 items-center flex-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center bg-[#f5f5f5] rounded-[12px] px-2 py-1"
                >
                  <span className="text-sm text-[#1E1E1E] font-[400]">
                    {option.label}
                  </span>
                  <button
                    type="button"
                    className={`ml-3.5 text-[#9e9e9e] ${
                      disabled ? "" : "hover:text-gray-700"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(option.value);
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 4L4 12M4 4L12 12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              !allowCustomInput && (
                <span className="text-[#B3B3B3] text-[14px]">
                  {placeholder}
                </span>
              )
            )}
            {allowCustomInput && !disabled && (
              <input
                type="text"
                className="flex-1 min-w-[120px] border-none outline-none text-sm text-[#1E1E1E] placeholder:text-[#B3B3B3] bg-transparent"
                placeholder={selectedOptions.length === 0 ? placeholder : ""}
                value={customInput}
                onChange={(e) => {
                  setCustomInput(e.target.value);
                  if (customInputError) setCustomInputError("");
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddCustomValue();
                  }
                }}
                onBlur={() => {
                  if (allowCustomInput && !disabled) {
                    handleAddCustomValue();
                  }
                }}
              />
            )}
          </div>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {!disabled && !hideOptionsDropdown && (
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            )}
          </div>
        </div>

        {isOpen && !hideOptionsDropdown && (
          <div
            ref={dropdownRef}
            className={`absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg scrollbar-custom ${
              showAbove ? "bottom-[42px]" : "top-[42px]"
            }`}
            style={{
              maxHeight:
                windowHeight < 630
                  ? `${Math.min(parseInt(maxHeight), windowHeight * 0.4)}px`
                  : maxHeight,
              overflowY: "auto",
              maxWidth: "100%",
            }}
          >
            <div className="sticky top-0 py-3 px-2.5 bg-white border-b border-[#E0E0E0] z-10">
              <div className="relative">
                <div className="absolute pl-2 inset-y-0 left-0 flex items-center pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full pl-7 pr-3 py-2 rounded-md text-sm focus:outline-none placeholder:text-[#B3B3B3]"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="p-2">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex h-[34px] items-center px-2 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCheckboxChange(option.value);
                    }}
                  >
                    {value.includes(option.value) ? (
                      <CheckboxCheckedSVG />
                    ) : (
                      <CheckboxUncheckedSVG />
                    )}

                    <div className="ml-2 block text-sm font-[500] text-[#1E1E1E] cursor-pointer">
                      {option.label}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">{error}</p>
      )}
      {!error && customInputError && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">
          {customInputError}
        </p>
      )}
    </div>
  );
};

export default SelectWithCheckbox;
